import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider,
  FormControl, InputLabel, MenuItem, Paper, Select,
  Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
  TextField, Tooltip, Typography,
} from '@mui/material'
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore'
import ExpandLessIcon   from '@mui/icons-material/ExpandLess'
import DownloadingIcon  from '@mui/icons-material/Downloading'
import { supabase }     from '../../../supabaseClient'
import { useAuth }      from '../../../auth/AuthContext'



// ── Types ──────────────────────────────────────────────────────────────────────

interface ScienceFair {
  id:              string
  name:            string
  website:         string | null
  county:          string | null
  state:           string | null
  crawl_status:    string | null
  last_crawled_at: string | null
}

interface CsefPanel {
  id:            string
  year:          number
  panel_id:      string
  category:      string
  division:      string
  status:        string
  project_count: number | null
  imported_at:   string | null
  error_msg:     string | null
}

interface HistoricalProject {
  project_code:      string
  title:             string
  grade:             number | null
  award_level:       string | null
  county:            string | null
  research_question: string | null
  methodology:       string | null
  real_world_hook:   string | null
}

// ── Status chip ────────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string | null }) {
  const map: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'default' }> = {
    success:   { label: 'Crawled',        color: 'success' },
    manual:    { label: 'Manual',         color: 'success' },
    failed:    { label: 'Crawl Failed',   color: 'error'   },
    pending:   { label: 'Pending',        color: 'default' },
    importing: { label: 'Importing…',    color: 'warning' },
    imported:  { label: 'Imported',       color: 'success' },
    error:     { label: 'Error',          color: 'error'   },
  }
  const s = map[status ?? 'pending'] ?? { label: status ?? 'Unknown', color: 'default' as const }
  return <Chip label={s.label} color={s.color} size="small" />
}

// ── Science Fairs tab ──────────────────────────────────────────────────────────

function ScienceFairsTab() {
  const [fairs,    setFairs]    = useState<ScienceFair[]>([])
  const [loading,  setLoading]  = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [saving,   setSaving]   = useState<string | null>(null)
  const [edits,    setEdits]    = useState<Record<string, Partial<ScienceFair>>>({})

  useEffect(() => {
    supabase.from('science_fair')
      .select('id, name, website, county, state, crawl_status, last_crawled_at')
      .order('name')
      .then(({ data }) => { setFairs(data ?? []); setLoading(false) })
  }, [])

  function edit(id: string, field: keyof ScienceFair, value: string) {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }))
  }

  async function save(fair: ScienceFair) {
    setSaving(fair.id)
    const patch = edits[fair.id] ?? {}
    await supabase.from('science_fair').update(patch).eq('id', fair.id)
    setFairs(prev => prev.map(f => f.id === fair.id ? { ...f, ...patch } : f))
    setEdits(prev => { const next = { ...prev }; delete next[fair.id]; return next })
    setSaving(null)
  }

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {fairs.length} science fair{fairs.length !== 1 ? 's' : ''} in the database.
        Fairs with <strong>crawl failed</strong> need manual data entry.
      </Typography>

      {fairs.map(fair => {
        const patch    = edits[fair.id] ?? {}
        const current  = { ...fair, ...patch }
        const isDirty  = Object.keys(patch).length > 0
        const isOpen   = expanded === fair.id

        return (
          <Paper key={fair.id} variant="outlined" sx={{ mb: 2 }}>
            {/* Header */}
            <Box
              sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
              onClick={() => setExpanded(isOpen ? null : fair.id)}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{fair.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[fair.county, fair.state].filter(Boolean).join(', ')}
                  {fair.last_crawled_at ? ` · Last crawled ${new Date(fair.last_crawled_at).toLocaleDateString()}` : ''}
                </Typography>
              </Box>
              <StatusChip status={fair.crawl_status} />
              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>

            <Collapse in={isOpen}>
              <Divider />
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Name" value={current.name ?? ''} size="small" sx={{ flex: '1 1 240px' }}
                    onChange={e => edit(fair.id, 'name', e.target.value)}
                  />
                  <TextField
                    label="Website" value={current.website ?? ''} size="small" sx={{ flex: '1 1 240px' }}
                    onChange={e => edit(fair.id, 'website', e.target.value)}
                  />
                  <TextField
                    label="County" value={current.county ?? ''} size="small" sx={{ flex: '1 1 140px' }}
                    onChange={e => edit(fair.id, 'county', e.target.value)}
                  />
                  <TextField
                    label="State" value={current.state ?? ''} size="small" sx={{ width: 90 }}
                    onChange={e => edit(fair.id, 'state', e.target.value)}
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Crawl Status</InputLabel>
                    <Select
                      label="Crawl Status"
                      value={current.crawl_status ?? 'pending'}
                      onChange={e => edit(fair.id, 'crawl_status', e.target.value)}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="manual">Manual</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {isDirty && (
                    <Button variant="outlined" size="small" onClick={() => setEdits(prev => { const next = { ...prev }; delete next[fair.id]; return next })}>
                      Discard
                    </Button>
                  )}
                  <Button
                    variant="contained" size="small"
                    disabled={!isDirty || saving === fair.id}
                    onClick={() => save(fair)}
                  >
                    {saving === fair.id ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Paper>
        )
      })}
    </Box>
  )
}

// ── CSEF Import tab ────────────────────────────────────────────────────────────

const CSEF_YEARS = [2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010, 2009]

function CsefImportTab() {
  const { session }                         = useAuth()
  const [year,          setYear]            = useState(2019)
  const [panels,        setPanels]          = useState<CsefPanel[]>([])
  const [loading,       setLoading]         = useState(true)
  const [importing,     setImporting]       = useState<Set<string>>(new Set())
  const [importAll,     setImportAll]       = useState(false)
  const [error,         setError]           = useState<string | null>(null)
  const [expandedPanel, setExpandedPanel]   = useState<string | null>(null)

  useEffect(() => { loadPanels(year) }, [year])

  async function loadPanels(y: number) {
    setLoading(true)
    const { data } = await supabase
      .from('csef_import_panel')
      .select('*')
      .eq('year', y)
      .order('panel_id')
    setPanels(data ?? [])
    setLoading(false)
  }

  async function importPanel(panel: CsefPanel) {
    if (!session) return
    setImporting(prev => new Set(prev).add(panel.panel_id))
    setError(null)

    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('import-csef-panel', {
        body: {
          year:     panel.year,
          panel_id: panel.panel_id,
          category: panel.category,
          division: panel.division,
          user_id:  session?.user?.id,
        },
      })

      if (invokeErr || data?.error) throw new Error(invokeErr?.message ?? data?.error ?? 'Import failed')

      // Refresh this panel's status
      setPanels(prev => prev.map(p =>
        p.panel_id === panel.panel_id
          ? { ...p, status: 'imported', project_count: data.project_count, imported_at: new Date().toISOString() }
          : p
      ))
    } catch (e: any) {
      setError(`${panel.panel_id}: ${e.message}`)
      setPanels(prev => prev.map(p =>
        p.panel_id === panel.panel_id ? { ...p, status: 'error', error_msg: e.message } : p
      ))
    } finally {
      setImporting(prev => { const next = new Set(prev); next.delete(panel.panel_id); return next })
    }
  }

  async function importAllPending() {
    setImportAll(true)
    const pending = panels.filter(p => p.status === 'pending' || p.status === 'error')
    for (let i = 0; i < pending.length; i++) {
      await importPanel(pending[i])
      if (i < pending.length - 1) {
        // 5s gap between panels — Haiku rate limit window resets quickly but batching fast causes 429s
        await new Promise(r => setTimeout(r, 5_000))
      }
    }
    setImportAll(false)
  }

  const juniorPanels  = panels.filter(p => p.division === 'Junior')
  const seniorPanels  = panels.filter(p => p.division === 'Senior')
  const importedCount = panels.filter(p => p.status === 'imported').length
  const totalProjects = panels.reduce((sum, p) => sum + (p.project_count ?? 0), 0)

  return (
    <Box>
      {/* Corpus header */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        California State Science Fair Corpus (2009 – 2019)
      </Typography>

      {/* Year selector */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 3 }}>
        {CSEF_YEARS.map(y => (
          <Chip
            key={y}
            label={y}
            onClick={() => setYear(y)}
            color={y === year ? 'primary' : 'default'}
            variant={y === year ? 'filled' : 'outlined'}
            size="small"
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
      <>
      {/* Year actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {importedCount} of {panels.length} panels imported · {totalProjects.toLocaleString()} projects
        </Typography>
        <Button
          variant="contained" size="small"
          onClick={importAllPending}
          disabled={importAll || panels.every(p => p.status === 'imported')}
          startIcon={importAll ? <CircularProgress size={14} color="inherit" /> : <DownloadingIcon />}
        >
          {importAll ? 'Importing…' : 'Import All Pending'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Progress bar */}
      <Box sx={{ mb: 3, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden', height: 6 }}>
        <Box sx={{ height: '100%', bgcolor: 'success.main', width: `${panels.length ? (importedCount / panels.length) * 100 : 0}%`, transition: 'width 0.4s' }} />
      </Box>

      {/* Junior panels */}
      <PanelTable
        title="Junior Division"
        year={year}
        panels={juniorPanels}
        importing={importing}
        onImport={importPanel}
        expandedPanel={expandedPanel}
        onExpandPanel={id => setExpandedPanel(expandedPanel === id ? null : id)}
      />

      <Box sx={{ mt: 3 }} />

      {/* Senior panels */}
      <PanelTable
        title="Senior Division"
        year={year}
        panels={seniorPanels}
        importing={importing}
        onImport={importPanel}
        expandedPanel={expandedPanel}
        onExpandPanel={id => setExpandedPanel(expandedPanel === id ? null : id)}
      />
      </>
      )}
    </Box>
  )
}

const AWARD_COLOR: Record<string, 'warning' | 'default' | 'primary'> = {
  first:             'warning',
  second:            'primary',
  third:             'default',
  fourth:            'default',
  honorable_mention: 'default',
}
const AWARD_LABEL: Record<string, string> = {
  first:             '1st',
  second:            '2nd',
  third:             '3rd',
  fourth:            '4th',
  honorable_mention: 'HM',
}

function ProjectRows({ year, panel_id }: { year: number; panel_id: string }) {
  const [projects, setProjects] = useState<HistoricalProject[]>([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    supabase
      .from('historical_project')
      .select('project_code, title, grade, award_level, county, research_question, methodology, real_world_hook')
      .eq('source_fair', 'California State Science & Engineering Fair')
      .eq('year', year)
      .eq('panel_id', panel_id)
      .order('award_level', { nullsFirst: false })
      .order('project_code')
      .then(({ data }) => { setProjects(data ?? []); setLoading(false) })
  }, [year, panel_id])

  if (loading) return (
    <TableRow>
      <TableCell colSpan={6} sx={{ py: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
        <CircularProgress size={18} />
      </TableCell>
    </TableRow>
  )

  if (!projects.length) return (
    <TableRow>
      <TableCell colSpan={6} sx={{ py: 1.5, pl: 4, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary">No projects imported yet.</Typography>
      </TableCell>
    </TableRow>
  )

  return (
    <>
      {projects.map(p => (
        <TableRow key={p.project_code} sx={{ bgcolor: 'grey.50' }}>
          <TableCell sx={{ pl: 4 }}>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              {p.project_code}
            </Typography>
          </TableCell>
          <TableCell colSpan={2}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {p.award_level && (
                  <Chip
                    label={AWARD_LABEL[p.award_level] ?? p.award_level}
                    color={AWARD_COLOR[p.award_level] ?? 'default'}
                    size="small"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.title}</Typography>
              </Box>
              {p.research_question && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {p.research_question}
                </Typography>
              )}
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="caption" color="text.secondary">
              {p.grade ? `Gr. ${p.grade}` : ''}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="caption" color="text.secondary">{p.county ?? ''}</Typography>
          </TableCell>
          <TableCell />
        </TableRow>
      ))}
    </>
  )
}

function PanelTable({
  title, year, panels, importing, onImport, expandedPanel, onExpandPanel,
}: {
  title:          string
  year:           number
  panels:         CsefPanel[]
  importing:      Set<string>
  onImport:       (panel: CsefPanel) => void
  expandedPanel:  string | null
  onExpandPanel:  (id: string) => void
}) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 60 }}>Panel</TableCell>
            <TableCell>Category</TableCell>
            <TableCell sx={{ width: 100 }}>Status</TableCell>
            <TableCell sx={{ width: 80 }}>Projects</TableCell>
            <TableCell sx={{ width: 140 }}>Imported</TableCell>
            <TableCell sx={{ width: 110 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {panels.map(panel => {
            const isImporting = importing.has(panel.panel_id)
            const isExpanded  = expandedPanel === panel.panel_id
            const canExpand   = panel.status === 'imported'
            return (
              <React.Fragment key={panel.panel_id}>
                <TableRow
                  sx={{
                    cursor:     canExpand ? 'pointer' : 'default',
                    bgcolor:    isExpanded ? 'primary.50' : undefined,
                    '&:hover':  { bgcolor: canExpand ? 'primary.50' : 'grey.50' },
                  }}
                  onClick={() => canExpand && onExpandPanel(panel.panel_id)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {canExpand && (isExpanded ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />)}
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {panel.panel_id}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{panel.category}</Typography>
                    {panel.error_msg && (
                      <Tooltip title={panel.error_msg}>
                        <Typography variant="caption" color="error" sx={{ cursor: 'help' }}>
                          {panel.error_msg.slice(0, 60)}{panel.error_msg.length > 60 ? '…' : ''}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    {isImporting
                      ? <Chip label="Importing…" color="warning" size="small" icon={<CircularProgress size={12} />} />
                      : <StatusChip status={panel.status} />
                    }
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={panel.project_count === 0 && panel.status === 'imported' ? 'text.secondary' : 'text.primary'}
                    >
                      {panel.project_count ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {panel.imported_at ? new Date(panel.imported_at).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Button
                      size="small" variant="outlined"
                      disabled={isImporting || panel.status === 'imported'}
                      onClick={() => onImport(panel)}
                    >
                      {panel.status === 'imported' ? 'Done' : panel.status === 'error' ? 'Retry' : 'Import'}
                    </Button>
                  </TableCell>
                </TableRow>
                {isExpanded && <ProjectRows year={year} panel_id={panel.panel_id} />}
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

// ── Main admin page ────────────────────────────────────────────────────────────

export default function ScienceFairsAdminPage() {
  const [tab, setTab] = useState(0)

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1000 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Science Fair Admin</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage science fair data and import historical project abstracts.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tab label="Science Fairs" />
        <Tab label="CSEF" />
      </Tabs>

      {tab === 0 && <ScienceFairsTab />}
      {tab === 1 && <CsefImportTab />}
    </Box>
  )
}
